import React, { useRef, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { L, tr } from "@/lib/i18n";
import useDialogFocus from "@/hooks/useDialogFocus";

/**
 * Lead capture form — modal panel opened from a unit detail or the top
 * "Enquire" CTA. Submits to `/public/leads` with full buyer-journey
 * context (Phase B Feature 7). Extracted verbatim from `PublicViewer.jsx`
 * during the iter-38 refactor.
 */
export default function LeadFormPanel({ project, unit, onClose, trackEvent, lang, journey, shortlistCodes, sessionId, activeSceneId }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, onClose);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const phoneDigits = (form.phone.match(/\p{Nd}/gu) || []).length;
    if (!form.name.trim() || phoneDigits < 6 || phoneDigits > 15) { setErr(L(lang, "lead_invalid")); return; }
    setLoading(true); setErr("");
    try {
      await api.post("/public/leads", {
        project_slug: project.slug, entity_id: unit?.id || null,
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() || null,
        message: form.message, language: lang,
        source: unit ? "unit_detail" : "public_viewer",
        // Phase B Feature 7 — buyer journey context.
        unit_code: unit?.code || null,
        scene_id: activeSceneId || null,
        session_id: sessionId || null,
        source_url: typeof window !== "undefined" ? window.location.href : null,
        plan_opened: !!journey?.plan_opened,
        gallery_opened: !!journey?.gallery_opened,
        video_opened: !!journey?.video_opened,
        pdf_downloaded: !!journey?.pdf_downloaded,
        whatsapp_clicked: !!journey?.whatsapp_clicked,
        shortlisted_codes: shortlistCodes || [],
        compared_codes: journey?.compared_codes || [],
        selected_payment_plan_id: journey?.selected_payment_plan_id || null,
      });
      trackEvent("lead_submission", { entity_id: unit?.id });
      setDone(true);
    } catch (e) {
      setErr(L(lang, "lead_failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={onClose} data-testid="lead-form-panel">
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="lead-dialog-title" className="w-full max-w-md max-h-[calc(100dvh-3rem)] overflow-y-auto glass-panel rounded-2xl p-7" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">{L(lang, "enquire")}</div>
            <div id="lead-dialog-title" className="font-display text-2xl text-white"><bdi>{unit ? unit.code : tr(project, "name", lang, project.default_language)}</bdi></div>
          </div>
          <button aria-label={L(lang, "close")} onClick={onClose} className="text-white/70 hover:text-white" data-testid="lead-close"><X className="h-4 w-4" /></button>
        </div>

        {done ? (
          <div className="mt-6 text-white text-sm" data-testid="lead-success">
            {L(lang, "lead_thanks")}
            <button onClick={onClose} className="mt-5 w-full px-4 py-2.5 rounded-full bg-[#C4A47C] text-zinc-900 text-xs uppercase tracking-wider">{L(lang, "lead_continue")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input required aria-label={L(lang, "lead_name")} autoComplete="name" maxLength={200} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={L(lang, "lead_name")} className="lead-input" data-testid="lead-name" />
            <input required type="tel" dir="ltr" aria-label={L(lang, "lead_phone")} autoComplete="tel" maxLength={60} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder={L(lang, "lead_phone")} className="lead-input" data-testid="lead-phone" />
            <input type="email" dir="ltr" aria-label={L(lang, "lead_email")} autoComplete="email" maxLength={320} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder={L(lang, "lead_email")} className="lead-input" data-testid="lead-email" />
            <textarea aria-label={L(lang, "lead_message")} maxLength={5000} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={L(lang, "lead_message")} className="lead-input h-24" data-testid="lead-message" />
            {err && <div role="alert" className="text-red-300 text-xs">{err}</div>}
            <button type="submit" disabled={loading} className="w-full px-4 py-2.5 rounded-full bg-[#C4A47C] text-zinc-900 text-xs uppercase tracking-wider hover:bg-[#d1b48d] transition inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="lead-submit">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {L(lang, "loading_action")}</> : <><Send className="h-4 w-4" /> {L(lang, "lead_send")}</>}
            </button>
            <style>{`.lead-input { width:100%; padding: 0.7rem 0.9rem; border-radius: 12px; background: rgba(255,255,255,0.06); color: #fff; border: 1px solid rgba(255,255,255,0.12); outline: none; }
            .lead-input::placeholder { color: rgba(255,255,255,0.4); }
            .lead-input:focus { border-color: rgba(196,164,124,0.6); }`}</style>
          </form>
        )}
      </div>
    </div>
  );
}
